import { useState } from "react";
import { Link } from "react-router-dom";
import { Stack, Typography } from "@mui/material";
import { toast } from "react-toastify";
import AuthShell, {
    AuthField,
    AuthSubmit,
    LockIcon,
    MailIcon,
    ShieldNote,
} from "../components/AuthShell";
import OtpStep from "../components/OtpStep";
import { login } from "../api";

/**
 * Sign in — split-screen (Login.dc.html). Step 1 asks for credentials; a
 * successful login always requires the OTP step (Verify.dc.html).
 */
export default function SignIn() {
    const [step, setStep] = useState("credentials"); // credentials | otp
    const [form, setForm] = useState({ email: "", password: "" });
    const [showPassword, setShowPassword] = useState(false);
    const [pendingToken, setPendingToken] = useState("");
    const [otpEmail, setOtpEmail] = useState("");
    const [loading, setLoading] = useState(false);

    const set = (key) => (e) =>
        setForm((prev) => ({ ...prev, [key]: e.target.value }));

    async function handleSubmit(e) {
        e.preventDefault();
        setLoading(true);
        try {
            const { data } = await login(form.email, form.password);
            if (data?.otpRequired && data?.pendingToken) {
                setPendingToken(data.pendingToken);
                setOtpEmail(data.email || form.email);
                setStep("otp");
            } else {
                toast.error("Unexpected response from the server");
            }
        } catch (err) {
            toast.error(
                err.response?.data?.error ||
                    err.response?.data?.message ||
                    "Invalid email or password",
            );
        } finally {
            setLoading(false);
        }
    }

    if (step === "otp") {
        return (
            <AuthShell
                headline={
                    <>
                        One code away from <em>your</em> workspace.
                    </>
                }
                sub="We verify every sign-in with a one-time code, so your customer data stays exactly where it belongs."
                bottomSlot={<ShieldNote />}
            >
                <OtpStep
                    email={otpEmail}
                    pendingToken={pendingToken}
                    setPendingToken={setPendingToken}
                    onBack={() => {
                        setStep("credentials");
                        setForm((prev) => ({ ...prev, password: "" }));
                    }}
                />
            </AuthShell>
        );
    }

    return (
        <AuthShell
            headline={
                <>
                    Build the CRM your team <em>actually</em> needs.
                </>
            }
            sub="Objects, roles, pipelines and automations — all defined by you, changed whenever your process changes."
        >
            <Stack
                component="form"
                onSubmit={handleSubmit}
                sx={{ width: "100%", maxWidth: 420, gap: 4 }}
            >
                <Stack sx={{ gap: "10px" }}>
                    <Typography
                        sx={{
                            fontFamily: "'Newsreader', Georgia, serif",
                            fontSize: 38,
                            fontWeight: 400,
                            letterSpacing: "-0.5px",
                        }}
                    >
                        Welcome back
                    </Typography>
                    <Typography
                        sx={{ fontSize: 15, color: "#6E6B66", lineHeight: 1.6 }}
                    >
                        Sign in to your workspace. We&#39;ll send a one-time
                        code to verify it&#39;s you.
                    </Typography>
                </Stack>

                <Stack sx={{ gap: "20px" }}>
                    <AuthField
                        label="WORK EMAIL"
                        icon={<MailIcon />}
                        type="email"
                        required
                        autoFocus
                        autoComplete="email"
                        placeholder="you@company.com"
                        value={form.email}
                        onChange={set("email")}
                    />
                    <AuthField
                        label="PASSWORD"
                        icon={<LockIcon />}
                        type={showPassword ? "text" : "password"}
                        required
                        autoComplete="current-password"
                        placeholder="••••••••••"
                        value={form.password}
                        onChange={set("password")}
                        endSlot={
                            <Stack
                                component="button"
                                type="button"
                                onClick={() => setShowPassword((s) => !s)}
                                aria-label={
                                    showPassword
                                        ? "Hide password"
                                        : "Show password"
                                }
                                sx={{
                                    border: "none",
                                    background: "none",
                                    cursor: "pointer",
                                    p: 0,
                                    display: "flex",
                                }}
                            >
                                <svg
                                    width="18"
                                    height="18"
                                    viewBox="0 0 20 20"
                                    fill="none"
                                    stroke="#8B8781"
                                    strokeWidth="1.5"
                                >
                                    <path d="M2 10s3-5.5 8-5.5S18 10 18 10s-3 5.5-8 5.5S2 10 2 10z" />
                                    {showPassword ? (
                                        <path d="M4 16 16 4" />
                                    ) : (
                                        <circle cx="10" cy="10" r="2.5" />
                                    )}
                                </svg>
                            </Stack>
                        }
                    />
                </Stack>

                <Stack sx={{ gap: "18px" }}>
                    <AuthSubmit loading={loading}>
                        {loading ? "Signing in…" : "Continue"}
                    </AuthSubmit>
                </Stack>

                <Typography
                    sx={{
                        fontSize: 14,
                        color: "#6E6B66",
                        textAlign: "center",
                    }}
                >
                    New to Tessera?{" "}
                    <Typography
                        component={Link}
                        to="/signup"
                        sx={{
                            fontSize: 14,
                            fontWeight: 600,
                            color: "#171715",
                            borderBottom: "1px solid #171715",
                            pb: "1px",
                            "&:hover": { color: "#1E6B4F" },
                        }}
                    >
                        Create a workspace
                    </Typography>
                </Typography>
            </Stack>
        </AuthShell>
    );
}
